// Coach approvals — the destination for the admin rail's "Approvals" item.
// File: frontend/src/pages/AdminApprovalsPage.js
//
// That nav item used to point at /admin/users?approval=pending, which lands on
// User Management filtered by the ATHLETE approval flag — a different queue
// entirely from the coach-review one the badge counts. Clicking "Approvals"
// and getting a user table was the reported bug.
//
// Two sections, from two sources:
//   Pending  — GET /api/admin/coach-review/pending (users with role
//              basic_coach). Approve/deny post to the same endpoints the
//              flags page uses; both mail the coach, so they are real actions.
//   Approved — GET /api/admin/users?role=coach, the coaches that review
//              already let through.
//
// ⚠️ THE APPROVED LIST IS NOT ORDERED BY APPROVAL DATE, and does not claim to
// be. `_do_coach_review_action` (backend/app/routers/admin.py) flips
// `role` -> COACH and `is_approved` -> True and commits; nothing records WHEN.
// The User model carries `created_at` only. So the date shown is the date they
// SIGNED UP and is labelled that way. Ordering by it is a reasonable proxy —
// a coach approved today almost certainly applied recently — but it is a
// proxy, and writing "Approved 3d ago" off `created_at` would be inventing a
// number. Adding an `approved_at` column is the real fix; it can only ever be
// populated going forward, so the 19 coaches already approved would stay NULL.
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import AdminPage from '../components/AdminShell';
import Button from '../components/Button';

const fmtDate = (iso) => {
  if (!iso) return '—';
  // Server timestamps are UTC without a tz suffix; append Z so this renders in
  // the admin's local zone instead of showing the UTC wall clock verbatim.
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

export default function AdminApprovalsPage() {
  const { API_URL } = useAuth();
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busyId, setBusyId] = useState(null);

  const cfg = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [p, a] = await Promise.all([
        axios.get(`${API_URL}/admin/coach-review/pending`, cfg()),
        axios.get(`${API_URL}/admin/users?role=coach&limit=50&sort=created_at&sort_dir=desc`, cfg()),
      ]);
      setPending(Array.isArray(p.data) ? p.data : []);
      const d = a.data;
      setApproved(Array.isArray(d) ? d : (d?.items || []));
    } catch (e) {
      // A failed fetch must not fall through to the empty-state copy — an
      // empty "Pending" panel reads as "nothing to review", which is the one
      // wrong conclusion to draw from a request that never answered.
      setError(e?.response?.data?.detail || 'Could not load approvals. Try again.');
      setPending([]);
      setApproved([]);
    } finally {
      setLoading(false);
    }
  }, [API_URL, cfg]);

  useEffect(() => { load(); }, [load]);

  const act = async (coach, action) => {
    const name = `${coach.first_name || ''} ${coach.last_name || ''}`.trim() || coach.email;
    // Both actions email the coach, so neither is undoable from this page.
    if (!window.confirm(
      action === 'approve'
        ? `Approve ${name} as a coach? They will be emailed.`
        : `Deny ${name}? Their role is set back to athlete and they will be emailed.`
    )) return;
    setBusyId(coach.id);
    setMessage('');
    try {
      const r = await axios.post(`${API_URL}/admin/coach-review/${coach.id}/${action}`, {}, cfg());
      setMessage(r.data?.message || `${name} ${action}d`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || `Could not ${action} ${name}.`);
    } finally {
      setBusyId(null);
    }
  };

  const subtitle = loading
    ? 'Loading…'
    : `${pending.length} pending · ${approved.length} approved coach${approved.length === 1 ? '' : 'es'}`;

  return (
    <AdminPage active="approvals" title="Coach Approvals" subtitle={subtitle}>
      {error && <div className="adm-error">{error}</div>}
      {message && <div className="adm__card adm__card--compact">{message}</div>}

      <div className="adm__card adm__card--flush" style={{ marginBottom: 'var(--adm-gap)' }}>
        <div className="adm-card__head">
          <h2 className="adm-card__title">Pending review</h2>
          {pending.length > 0 && (
            <span className="adm-chip adm-chip--amber">{pending.length} waiting</span>
          )}
        </div>

        {loading ? (
          <div className="adm-loading">Loading…</div>
        ) : pending.length === 0 ? (
          <div className="adm-empty">No coach applications waiting. Nothing to do here.</div>
        ) : (
          <div className="adm-tablewrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Coach</th><th>School</th><th>Location</th>
                  <th>Applied</th><th className="adm-th--right">Decision</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((c) => {
                  const name = `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email;
                  const flagged = (c.sex_offender_matches || []).length > 0;
                  return (
                    <tr key={c.id}>
                      <td>
                        <div className="adm-cell__name">
                          {name}
                          {flagged && (
                            <span className="adm-chip adm-chip--red" style={{ marginLeft: 6 }}>
                              {c.sex_offender_matches.length} registry match
                            </span>
                          )}
                        </div>
                        <div className="adm-cell__meta">{c.email}</div>
                      </td>
                      <td>{c.school || '—'}</td>
                      <td>{[c.city, c.state].filter(Boolean).join(', ') || '—'}</td>
                      <td>{fmtDate(c.created_at)}</td>
                      <td className="adm-td--right">
                        <div style={{ display: 'inline-flex', gap: 8 }}>
                          <Button variant="accent" size="sm" disabled={busyId === c.id}
                            onClick={() => act(c, 'approve')}>Approve</Button>
                          <Button variant="danger" size="sm" disabled={busyId === c.id}
                            onClick={() => act(c, 'deny')}>Deny</Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="adm__card adm__card--flush">
        <div className="adm-card__head">
          <h2 className="adm-card__title">Approved coaches</h2>
          <span className="adm-card__meta">{approved.length}</span>
        </div>

        {loading ? (
          <div className="adm-loading">Loading…</div>
        ) : approved.length === 0 ? (
          <div className="adm-empty">No approved coaches yet.</div>
        ) : (
          <div className="adm-tablewrap">
            <table className="adm-table">
              <thead>
                <tr><th>Coach</th><th>School</th><th>Location</th><th>Signed up</th></tr>
              </thead>
              <tbody>
                {approved.map((u) => {
                  const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
                  return (
                    <tr key={u.id}>
                      <td>
                        <div className="adm-cell__name">{name}</div>
                        <div className="adm-cell__meta">{u.email}</div>
                      </td>
                      <td>{u.school || '—'}</td>
                      <td>{[u.city, u.state].filter(Boolean).join(', ') || '—'}</td>
                      <td>{fmtDate(u.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {/* Says what the column IS. See the header comment: there is no
            approved_at anywhere in the schema to sort or label by. */}
        <p className="adm-note">
          Sorted by sign-up date — the schema records no approval timestamp, so this
          is not "recently approved".
        </p>
      </div>
    </AdminPage>
  );
}
